// src/App.js
import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Route, Routes } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import './App.css';
import SpotlightCursor from './components/SpotlightCursor';
import Navbar from './components/Navbar';
import { Footer } from './components/Footer';
import HomePage from './pages/HomePage';
import CookieConsent from './components/CookieConsent';
import PricingPage from './pages/PricingPage';
import RewardsPage from './pages/RewardsPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import ResultsPage from './pages/ResultsPage';
import SplashScreen from './components/SplashScreen';
import PageTransition from './components/PageTransition';
import InsightsPage from './pages/InsightsPage';
import InsightAiCryptoSignalsPage from "./pages/InsightAiCryptoSignalsPage";
import AboutPage from "./pages/AboutPage";
import InsightRiskManagementSmallAccountsPage from "./pages/InsightRiskManagementSmallAccountsPage";
import InsightCryptoSignalsBeginnersPage from "./pages/InsightCryptoSignalsBeginnersPage";
import InsightAiTradingSignalsAndroidPage from "./pages/InsightAiTradingSignalsAndroidPage";
import InsightAiStockAnalysisAndroidPage from "./pages/InsightAiStockAnalysisAndroidPage";
import InsightCryptoSwingTradingSignalsPage from "./pages/InsightCryptoSwingTradingSignalsPage";
import InsightTradingJournalAiSignalsPage from "./pages/InsightTradingJournalAiSignalsPage";
import InsightBestIndicatorsAiSignalsPage from "./pages/InsightBestIndicatorsAiSignalsPage";
import InsightBestCryptoTradingAppAndroidPage from "./pages/InsightBestCryptoTradingAppAndroidPage";
import InsightFibonacciTradingCryptoPage from "./pages/InsightFibonacciTradingCryptoPage";
import InsightDayTradingCryptoAiSignalsPage from "./pages/InsightDayTradingCryptoAiSignalsPage";
import InsightAiCryptoMarketAnalysisPage from "./pages/InsightAiCryptoMarketAnalysisPage";
import InsightCryptoTradingSignalsExplainedPage from "./pages/InsightCryptoTradingSignalsExplainedPage";
import InsightBitcoinAltcoinMarketIndicatorsPage from "./pages/InsightBitcoinAltcoinMarketIndicatorsPage";
import InsightCryptoMarketTrendsEmotionalTradingPage from "./pages/InsightCryptoMarketTrendsEmotionalTradingPage";
import InsightAiCryptoAnalysisToolsPage from "./pages/InsightAiCryptoAnalysisToolsPage";
import WhitepaperPage from "./pages/WhitepaperPage";
import TokenPage from "./pages/TgenTokenPage";
import ProductsPage from "./pages/ProductsPage";
import ProductDetailPage from "./pages/ProductDetailPage";
import NewsPage from "./pages/NewsPage";
import ContactPage from "./pages/ContactPage";
import ChatBotWidget from './components/ChatBotWidget';
import LivePriceIndicator from './components/LivePriceIndicator';
import CryptoBackground from './components/CryptoBackground';

// Inner component — must live inside <Router> so PageTransition can use useLocation
function AppContent() {
  return (
    <>
      <CryptoBackground />
      <div style={{ position: 'relative', zIndex: 1 }}>
      <PageTransition />
      <SpotlightCursor />
      <Helmet>
        <title>Trend Genie: AI Powered Investment Analysis App</title>
        <meta
          name="description"
          content="Make smarter trading decisions with Trend Genie. Get real time data, AI powered insights, and advanced charting for stocks and crypto, right on your Android phone."
        />
        <meta
          name="keywords"
          content="investment app, stock market analysis app, AI trading tool, crypto tracker, Trend Genie app"
        />
      </Helmet>

      <Navbar />
      <div style={{ height: '68px' }} aria-hidden="true" />
      <main>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/social" element={<PricingPage />} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/tgen-airdrop-live" element={<RewardsPage />} />
          <Route path="/rewards" element={<RewardsPage />} />
          <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
          <Route path="/live-results" element={<ResultsPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/news" element={<NewsPage />} />
          <Route path="/crypto-news" element={<NewsPage />} />
          <Route path="/insights" element={<InsightsPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/products" element={<ProductsPage />} />
          <Route
            path="/ai-crypto-signals-app"
            element={<ProductDetailPage productKey="android-app" />}
          />
          <Route
            path="/products/android-app"
            element={<ProductDetailPage productKey="android-app" />}
          />
          <Route
            path="/rugshield-defi-scanner"
            element={<ProductDetailPage productKey="rugshield-web" />}
          />
          <Route
            path="/products/rugshield-web"
            element={<ProductDetailPage productKey="rugshield-web" />}
          />
          <Route
            path="/products/social"
            element={<ProductDetailPage productKey="social" />}
          />
          <Route
            path="/products/nova"
            element={<ProductDetailPage productKey="nova" />}
          />
          <Route path="/whitepaper" element={<WhitepaperPage />} />
          <Route path="/tgen-token" element={<TokenPage />} />
          <Route path="/token" element={<TokenPage />} />
          <Route path="/insights/ai-crypto-signals-app-android" element={<InsightAiCryptoSignalsPage />} />
          <Route path="/insights/risk-management-small-trading-accounts" element={<InsightRiskManagementSmallAccountsPage />} />
          <Route path="/insights/crypto-signals-for-beginners" element={<InsightCryptoSignalsBeginnersPage />} />
          <Route path="/insights/ai-trading-signals-android" element={<InsightAiTradingSignalsAndroidPage />} />
          <Route path="/insights/ai-stock-analysis-app-android" element={<InsightAiStockAnalysisAndroidPage />} />
          <Route path="/insights/crypto-swing-trading-signals-strategy" element={<InsightCryptoSwingTradingSignalsPage />} />
          <Route path="/insights/trading-journal-ai-signals" element={<InsightTradingJournalAiSignalsPage />} />
          <Route path="/insights/best-indicators-confirm-ai-crypto-signals" element={<InsightBestIndicatorsAiSignalsPage />} />
          <Route path="/insights/best-crypto-trading-app-android" element={<InsightBestCryptoTradingAppAndroidPage />} />
          <Route path="/insights/fibonacci-trading-strategy-crypto" element={<InsightFibonacciTradingCryptoPage />} />
          <Route path="/insights/day-trading-crypto-ai-signals" element={<InsightDayTradingCryptoAiSignalsPage />} />
          <Route path="/insights/how-ai-crypto-market-analysis-works" element={<InsightAiCryptoMarketAnalysisPage />} />
          <Route path="/insights/crypto-trading-signals-explained" element={<InsightCryptoTradingSignalsExplainedPage />} />
          <Route path="/insights/bitcoin-altcoin-market-indicators" element={<InsightBitcoinAltcoinMarketIndicatorsPage />} />
          <Route path="/insights/reading-crypto-market-trends-without-emotional-trading" element={<InsightCryptoMarketTrendsEmotionalTradingPage />} />
          <Route path="/insights/ai-crypto-analysis-tools-for-traders-and-creators" element={<InsightAiCryptoAnalysisToolsPage />} />
        </Routes>
      </main>
      <Footer />
      <LivePriceIndicator />
      <ChatBotWidget />
      <CookieConsent />
      </div>
    </>
  );
}

function App() {
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const t = setTimeout(() => setLoading(false), 2200);
    return () => clearTimeout(t);
  }, []);

  if (loading) return <SplashScreen />;

  return (
    <Router>
      <AppContent />
    </Router>
  );
}

export default App;
